import {
    Component,
    input,
    output,
    ViewEncapsulation,
    ChangeDetectionStrategy,
    signal,
    computed,
    Optional,
    Self
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, FormsModule, NgControl } from '@angular/forms';
import { SelectButtonModule } from 'primeng/selectbutton';
import { SelectItem } from 'primeng/api';

@Component({
    selector: 'fcp-selectbutton',
    imports: [CommonModule, SelectButtonModule, FormsModule],
    styleUrl: './fcp-selectbutton.component.less',
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '[attr.data-e2e-hook]': 'e2e()',
        '[class.fcp-selectbutton--small]': 'size() === "small"'
    },
    template: `<p-selectbutton
        [ngModel]="modelValue()"
        [options]="options()"
        [optionLabel]="optionLabel()"
        [optionValue]="optionValue()"
        [optionDisabled]="optionDisabled()"
        [unselectable]="unselectable()"
        [tabindex]="tabindex()"
        [multiple]="multiple()"
        [allowEmpty]="allowEmpty()"
        [styleClass]="computedStyleClass()"
        [ariaLabelledBy]="ariaLabelledBy()"
        [size]="size()"
        [disabled]="isDisabled()"
        [class.p-invalid]="isInvalid"
        [class.ng-invalid]="isControlInvalidAndDirty"
        [class.ng-dirty]="isControlInvalidAndDirty"
        [dataKey]="dataKey()"
        [autofocus]="autofocus()"
        (ngModelChange)="handleChange($event)"
        (blur)="handleTouched()"
        (onOptionClick)="onOptionClick.emit($event)"
        (onChange)="handleOnChange($event)"
    >
        <ng-template #item let-item>
            @if (item.label) {
                <span>{{ item.label }}</span>
            }

            @if (item.icon) {
                <span [class]="item.icon"></span>
            }
        </ng-template>
    </p-selectbutton> `
})
export class FcpSelectbuttonComponent implements ControlValueAccessor {
    constructor(@Optional() @Self() private readonly ngControl: NgControl | null) {
        if (this.ngControl) {
            this.ngControl.valueAccessor = this;
        }
    }

    e2e = input<string | null>(null);
    options = input<SelectItem[]>();
    optionLabel = input<string>();
    optionValue = input<string>();
    optionDisabled = input<string>();
    unselectable = input<boolean>();
    tabindex = input<number>(0);
    multiple = input<boolean>(false);
    allowEmpty = input<boolean>(true);
    invalid = input<boolean>(false);
    styleClass = input<string>();
    ariaLabelledBy = input<string>();
    size = input<SelectButtonSize>(SelectButtonSize.Normal);
    disabled = input<boolean>(false);
    dataKey = input<string>();
    autofocus = input<boolean>(false);

    onOptionClick = output<SelectButtonOptionClickEvent>();
    onChange = output<SelectButtonChangeEvent>();

    private _value = signal<unknown>(null);
    private _onChange = signal<(value: unknown) => void>(() => {});
    private _onTouched = signal<() => void>(() => {});
    private _formDisabled = signal<boolean>(false);
    modelValue = computed(() => this._value());
    isDisabled = computed(() => this.disabled() || this._formDisabled());
    protected get isInvalid(): boolean {
        return this.invalid() || this.isControlInvalidAndDirty;
    }
    computedStyleClass = computed(() => {
        const baseClass = this.styleClass() ?? '';
        const smallClass = this.size() === SelectButtonSize.Small ? 'fcp-selectbutton--small' : '';
        return [baseClass, smallClass].filter((value) => value.trim().length > 0).join(' ');
    });

    writeValue(value: unknown): void {
        this._value.set(value);
    }

    registerOnChange(fn: (value: unknown) => void): void {
        this._onChange.set(fn);
    }

    registerOnTouched(fn: () => void): void {
        this._onTouched.set(fn);
    }

    setDisabledState(isDisabled: boolean): void {
        this._formDisabled.set(isDisabled);
    }

    handleChange(value: unknown): void {
        this._value.set(value);
        this._onChange()(value);
        this._onTouched()();
    }

    handleOnChange(event: SelectButtonChangeEvent): void {
        this.onChange.emit(event);
    }

    handleTouched(): void {
        this._onTouched()();
    }

    protected get isControlInvalidAndDirty(): boolean {
        const control = this.ngControl?.control ?? null;
        return !!control && control.invalid && (control.dirty || control.touched);
    }
}

export enum SelectButtonSize {
    Small = 'small',
    Normal = null
}

export interface SelectButtonOptionClickEvent {
    originalEvent: Event;
    option: unknown;
    index: number;
}

export interface SelectButtonChangeEvent {
    originalEvent: Event;
    value: unknown;
}
